// src/utilities/clientLanding.js — SOUND-726
//
// Where a signed-in merchant belongs, given the state of their application.
//
// Every successful auth navigates to `afterAuthPath`, which is a constant
// ("/registration"). That is right for someone still filling the form in and
// wrong for the 265 approved clients in prod, who land on a wizard they finished
// months ago and are bounced off it a moment later.
//
// The bounce itself lived inside useRegistration's fetch effect as
// `["submitted", "approved"].includes(status)` — four of the six statuses the
// API defines, decided as a side effect of loading data, in a hook, where
// nothing could test it. `needs_info` fell through that list by accident and
// happened to land correctly; `rejected` was handled on a separate branch.
//
// This is that decision, on its own, as a pure function.
//
// It MIRRORS sound-api config/statusMachines.mjs — specifically
// REGISTRATION_CLIENT_EDITABLE_STATUSES (`new`, `in_progress`, `needs_info`),
// which is the set the API will actually accept merchant edits for. Sending
// someone to the wizard outside that set shows them a form the API then 403s.
// If that constant changes, this changes with it.

export const LANDING_REGISTRATION = "/registration";
export const LANDING_DASHBOARD = "/dashboard";
export const LANDING_REJECTED = "/application-rejected";

/**
 * Statuses in which the merchant still has work to do in the wizard.
 *
 * `needs_info` is in here on purpose and is the easy one to drop: we asked them
 * for something, so the form is exactly where they need to be. The API agrees —
 * REGISTRATION_TRANSITIONS has `needs_info: ["submitted"]`, and the status only
 * exists to solicit that edit.
 */
const EDITABLE = new Set(["new", "in_progress", "needs_info"]);

/** Decided, and communicated. There is nothing for them to edit. */
const FINISHED = new Set(["submitted", "approved"]);

/**
 * The path a client should be on for a given application status.
 *
 * @param {string|null|undefined} status
 * @returns {string} one of the LANDING_* paths
 */
export function resolveClientLandingPath(status) {
  const s = typeof status === "string" ? status.trim() : "";

  if (EDITABLE.has(s)) return LANDING_REGISTRATION;
  if (FINISHED.has(s)) return LANDING_DASHBOARD;
  if (s === "rejected") return LANDING_REJECTED;

  // Unknown, missing, or null — and prod holds one application with a null
  // status. The wizard is the safe default: it is where an application that has
  // not started belongs, it is read-mostly, and the API is the thing that
  // actually enforces who may write. Guessing "dashboard" would strand a
  // genuine draft with no route back to the form.
  return LANDING_REGISTRATION;
}

/**
 * Whether a merchant may see the dashboard yet.
 *
 * The inverse of shouldLeaveRegistration, and it MUST be derived from the same
 * function rather than written as its own list. The two redirects point at each
 * other — /registration sends finished applications to /dashboard, /dashboard
 * sends unfinished ones back — so any disagreement between them is not a wrong
 * answer, it is an infinite loop. One source of truth makes that unrepresentable.
 *
 * Callers must handle "no application at all" themselves: a parent company has
 * none, and an unknown status resolves to the wizard, which would trap them.
 * This function answers only for a status that exists.
 *
 * @param {string|null|undefined} status
 * @returns {boolean}
 */
export function isDashboardAllowed(status) {
  return resolveClientLandingPath(status) === LANDING_DASHBOARD;
}

/**
 * Whether a merchant sitting on /registration should be sent somewhere else.
 *
 * Used to keep the wizard from rendering for a beat before the redirect fires —
 * a form that appears and vanishes reads as a glitch, and on a slow connection
 * it is long enough to start typing into.
 *
 * @param {string|null|undefined} status
 * @returns {boolean}
 */
export function shouldLeaveRegistration(status) {
  return resolveClientLandingPath(status) !== LANDING_REGISTRATION;
}
